import {
  NskStreamClientDelegate,
  PlatformDetails,
  getPlatformDetails,
  NskStreamClient,
  createNskStreamClient,
  StreamStartData,
  RErrorCode,
  StreamStopData,
  StreamStatistics,
  StreamQuality,
  StreamWarning,
  TextCompositionEvent,
  DownstreamVideoSettings,
  StreamConnectionInfo,
  UpstreamMediaSettings,
  SessionParameters,
  SessionControl,
  StreamStartParameters,
  registerLogCallback,
} from '@gamestream/ragnarok';
import {
  Box,
  Container,
  TextField,
  Button,
  CardHeader,
  Grid,
  Typography,
  Stack,
  ListItemText,
  ListItem,
  List,
  Switch,
} from '@mui/material';
import Card from '@mui/material/Card';
import React from 'react';

let platformDetails: PlatformDetails;
// @ts-ignore
let hiddenTextElement = undefined;
// Define a map of string vs string

/**
 * Session/Streaming properties derived from SampleClient settings page.
 */
interface ClientSessionParameters {
  /** GFN zone address or a passthru server IP or MockPM address. */
  serverAddress: string;
  serverPort: number;
  /* Indicates if the above appId can support raw touch inputs. 
     Ragnarok client will use this property to set appropriate AppLaunchMode while requesting a session. */
  appSupportsTouch: boolean;
  /* Passed to GFN server in session request. Not applicable in passthru use case. */
  persistInGameSettings: boolean;
  /* Passed to GFN server in session request. Not applicable in passthru use case. */
  accountLinked: boolean;
  /* resolution, frameRate, DRC settings etc. */
  downstreamVideoSettings: DownstreamVideoSettings;
  /* HTMLAudioElement Id where audio stream will be attached to in separate audio-video stream use case. */
  audioTagId: string;
  // /* Signaling connection information, if set will treat this as passthru use case, the serverAddress can be empty in this case. */
  signalConnectionInfo?: StreamConnectionInfo;
  // /* Media connection information to be used for WebRTC connection. */
  mediaConnectionInfo?: StreamConnectionInfo;
  // /* Optional sessionId to be passed to server. Omniverse use case requires this. */
  sessionId?: string;
  // /** Media to send to the server */
  upstreamMediaSettings?: UpstreamMediaSettings[];
}

interface RagnarokLogs {
  message: string;
  timestamp: string;
  level: string;
  tag: string;
}

// Define a type for the component's state
interface VideoAudioRecorderState {
  width: number;
  height: number;
  fps: number;
  serverAddress: string;
  serverPort: number;
  logs: Array<RagnarokLogs>;
  mediaAddress: string;
  mediaPort: number;
  useMediaConnectionInfo: boolean;
}

async function Setup() {
  await getPlatformDetails()
    .then((details: PlatformDetails) => {
      platformDetails = details;
      console.log('Platform details: ', platformDetails);
    })
    .catch((e) => {
      console.error('failed to get platform details', e);
    });
}

document.addEventListener('DOMContentLoaded', () => {
  Setup();
});

export default class Player extends React.Component<{}, VideoAudioRecorderState> implements NskStreamClientDelegate {
  private sessionControlAbortController?: AbortController;
  private sessionControl?: SessionControl;
  private sessionId: string = '';
  private subSessionId: string = '';
  // @ts-ignore
  private streamClient: NskStreamClient;
  private videoElement?: HTMLVideoElement;
  private audioElement?: HTMLAudioElement;
  /**
   * The server address with which this.sessionControl is initialized.
   * Need to cache this to reset sessionControl when switching between prod/staging and MockPM modes.
   */
  private serverAddress: string = '';

  constructor() {
    super(platformDetails, null);
    registerLogCallback((logs: RagnarokLogs) => {
      if (this.state.logs.length < 10000) {
        this.setState((prevState) => ({
          logs: [...prevState.logs, logs],
        }));
      } else {
        console.log('Clearing logs to avoid page hang');
        this.setState({ logs: [] });
      }
    });
    this.state = {
      width: 1920,
      height: 1080,
      fps: 30,
      serverAddress: window.location.hostname,
      serverPort: 49100,
      mediaAddress: window.location.hostname,
      mediaPort: 49100,
      logs: [],
      useMediaConnectionInfo: true,
    };
    Setup().then(() => {
      console.log(platformDetails);
      this.streamClient = createNskStreamClient(platformDetails, this, {
        // @ts-ignore
        textInputElement: hiddenTextElement,
      });
    });
  }

  handleWidthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ width: parseInt(e.target.value, 1920) });
  };

  handleHeightChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ height: parseInt(e.target.value, 1080) });
  };

  handleFpsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ fps: parseInt(e.target.value, 30) });
  };

  public playMedia(internalPlay?: boolean): void {
    // When stream connects we attempt to play video and audio elements automatically.
    // If play fails in this case then we request for user action.
    // If play was called from user action then any play error is treated as failure and we terminate stream.
    // Note: we don't check for navigator.userActivation.hasBeenActive as for iOS safari its only supported 16.4 onwards.
    let errorProcessed = false;

    const handlePlayError = (video: boolean, error: any) => {
      if (errorProcessed) {
        return;
      }
      errorProcessed = true;
      const media = video ? 'video' : 'audio';
      if (!internalPlay) {
        console.error(`Aborting streaming as ${media} play failed even after user action. error: ${error.message}`);
        // todo: define some client termination codes and appropriate user message.
        this.streamClient.stopStreaming(0x01);
      } else {
        console.error(`Requesting user action overlay as ${media} play failed.`);
      }
    };

    this.videoElement = document.getElementById('ragnarok-video-player') as HTMLVideoElement;
    this.audioElement = document.getElementById('ragnarok-audio-player') as HTMLAudioElement;

    this.videoElement
      .play()
      .then(() => {
        // this.streamClient.captureUserInput(true);
      })
      .catch((error: any) => {
        handlePlayError(true, error);
      });

    // we might not always have separate audio stream.
    if (this.audioElement.srcObject) {
      this.audioElement.play().catch((error: any) => {
        handlePlayError(false, error);
      });
    } else {
      console.log('no audio source object');
    }
  }

  public onStreamStarted(data: StreamStartData): void {
    console.log('onStreamStarted: ', data);
    // data.separateAudioStream = true;
    this.playMedia(true);
    // Notifies the client of streaming started. All the NskStreamClient functions which needs to be executed only
    // during streaming can be called after this. onStreamStopped function notifies that streaming has ended.
  }

  public onStreamStartFailed(errorCode: RErrorCode): void {
    console.log('onStreamStartFailed: ', errorCode);
    alert(`Stream start failed, error code: ${errorCode}`);
    // Notifies the streaming client of connection failure. Clients need to display appropriate message to the user.
  }

  public onStreamStopped(data: StreamStopData): void {
    // Notifies the streaming client of termination of stream. Streaming can be stopped due to various reason
  }

  public onStreamStatistics(stats: StreamStatistics): void {
    // console.log('onStreamStatistics: ', stats);
    // Provides the streaming stats to the clients periodically.
  }

  public onStreamQuality(quality: StreamQuality): void {
    // console.log('onStreamQuality: ', quality);
    //We don't support displaying network bars in test client yet.
  }

  public onStreamWarning(warning: StreamWarning): void {
    //we don't yet support informing users of idle warning timeout etc.
  }

  public onTextComposition(event: TextCompositionEvent): void {
    // Notifies the client that text composition is in progress or completed. This event can be used for displaying the
    // Client IME text input options. This notification is invoked for every composition update as user updates the input.
    // todo: the comments are not detailed enough, need info on which platforms provide this, how should client react to this.
  }

  private handleCreateSession = () => {
    this.streamClient.stopStreaming(0x01);
    // set video resolution and framerate
    const streamParam: DownstreamVideoSettings = {
      width: this.state.width,
      height: this.state.height,
      fps: this.state.fps,
      videoTagId: 'ragnarok-video-player',
    };

    console.log('stream params: ', streamParam);

    // set session parameters
    const params: ClientSessionParameters = {
      serverAddress: this.state.serverAddress,
      serverPort: this.state.serverPort,
      appSupportsTouch: false,
      persistInGameSettings: false,
      accountLinked: false,
      downstreamVideoSettings: streamParam,
      audioTagId: 'ragnarok-audio-player',
      sessionId: '',
    };
    console.log('Session parameters: ', params);
    this.setStreamingParameters(params);
  };

  private initiateStreaming(parameters: StreamStartParameters): void {
    const resultCode = this.streamClient.startStreaming(parameters);
    if (resultCode !== 15859712) {
      // should not happen as all the parameters are validated in SampleClient class.
      console.log('Error', resultCode);
      alert(`Can't start streaming, error code: ${resultCode}`);
    }
  }

  private setStreamingParameters(parameters: ClientSessionParameters) {
    this.sessionControl = undefined;

    const sessionParameters: SessionParameters = {
      appId: '',
      monitorSettings: [],
    };

    const streamStartParameters: StreamStartParameters = {
      downstreamParams: [parameters.downstreamVideoSettings],
      signalConnectionInfo: parameters.signalConnectionInfo ?? {
        address: parameters.serverAddress,
        port: parameters.serverPort,
      },
      enableTouchInput: false,
      sessionId: '402f4c9a-9626-48fd-8d54-4bdd6717c680',
      audioTagId: parameters.audioTagId,
      zoneAddress: '',
      resume: false,
      streamingFeatures: sessionParameters.streamingFeatures,
      upstreamParams: parameters.upstreamMediaSettings,
    };
    if (this.state.useMediaConnectionInfo) {
      streamStartParameters.mediaConnectionInfo = {
        address: this.state.mediaAddress,
        port: this.state.mediaPort,
      };
    }
    console.log('streamStartParameters', streamStartParameters);
    this.initiateStreaming(streamStartParameters);
  }

  private copyAllLogs = () => {
    const allLogs = this.state.logs.map((log) => `${log.timestamp} ${log.level} ${log.tag} ${log.message}`).join('\n');
    navigator.clipboard
      .writeText(allLogs)
      .then(() => alert('All logs copied to clipboard!'))
      .catch((err) => console.error('Failed to copy logs: ', err));
  };

  render() {
    const { width, height, fps, serverAddress, serverPort, logs, mediaAddress, mediaPort } = this.state;

    return (
      <Container maxWidth="xl">
        <Typography variant="h4" sx={{ mb: 5 }}>
          Ragnarok Player
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <Box sx={{ p: 3 }}>
                <Grid container spacing={3}>
                  <Grid item xs={6}>
                    <Stack spacing={3} sx={{ mb: 2 }}>
                      <TextField
                        label="Signalling Address"
                        type="text"
                        variant="outlined"
                        value={serverAddress}
                        onChange={(e) => this.setState({ serverAddress: e.target.value })}
                      />
                      <TextField
                        label="Signalling Port"
                        type="number"
                        variant="outlined"
                        value={serverPort}
                        onChange={(e) => this.setState({ serverPort: parseInt(e.target.value) })}
                      />
                      <TextField
                        label="Media Connection Address"
                        type="text"
                        variant="outlined"
                        value={mediaAddress}
                        onChange={(e) => this.setState({ mediaAddress: e.target.value })}
                      />
                      <TextField
                        label="Media Connection Port"
                        type="number"
                        variant="outlined"
                        value={mediaPort}
                        onChange={(e) => this.setState({ mediaPort: parseInt(e.target.value) })}
                      />
                    </Stack>
                  </Grid>
                  <Grid item xs={6}>
                    <Stack spacing={3} sx={{ mb: 2 }}>
                      <TextField
                        label="Width"
                        type="number"
                        variant="outlined"
                        value={width}
                        onChange={(e) => this.setState({ width: parseInt(e.target.value, 10) })}
                      />
                      <TextField
                        label="Height"
                        type="number"
                        variant="outlined"
                        value={height}
                        onChange={(e) => this.setState({ height: parseInt(e.target.value, 10) })}
                      />
                      <TextField
                        label="FPS"
                        type="number"
                        variant="outlined"
                        value={fps}
                        onChange={(e) => this.setState({ fps: parseInt(e.target.value, 10) })}
                      />
                      <Box>
                        Use Media Connection Info
                        <Switch
                          checked={this.state.useMediaConnectionInfo}
                          onChange={() => {
                            this.setState({ useMediaConnectionInfo: !this.state.useMediaConnectionInfo });
                          }}
                        />
                      </Box>
                    </Stack>
                  </Grid>
                  <Grid item xs={12}>
                    <Button variant="contained" color="primary" onClick={this.handleCreateSession} sx={{ mt: 2 }}>
                      Start
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Card>
          </Grid>

          <Grid item xs={12} sm={12} md={12}>
            <Card sx={{ mt: 2 }}>
              <CardHeader title="Player" />
              <video id="ragnarok-video-player" autoPlay muted style={{ maxWidth: '100%' }} />
              <audio id="ragnarok-audio-player" autoPlay muted />
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card sx={{ mt: 2 }}>
              <CardHeader
                title="Ragnarok logs"
                subheader="Format is timestamp, level, tag followed my message in next line"
              />
              <Box sx={{ maxHeight: 500, overflow: 'auto', width: '97%', border: '1px solid #ccc', m: 1 }}>
                <List>
                  {logs.map((item, index) => (
                    <ListItem key={index}>
                      <ListItemText primary={`${item.timestamp} ${item.level} ${item.tag}`} secondary={item.message} />
                    </ListItem>
                  ))}
                </List>
              </Box>
              <Box sx={{ m: 3 }}>
                <Button variant="contained" color="primary" onClick={this.copyAllLogs}>
                  Copy All Logs
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    this.setState({ logs: [] });
                  }}
                  sx={{ ml: 2 }}
                >
                  Clear
                </Button>
              </Box>
            </Card>
          </Grid>
        </Grid>
      </Container>
    );
  }
}
